import DefaultImage from "@assets/tm_article_default.svg";
import Image from "@elements/ResponsiveImage/Image";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { formatDateAgo } from "@helper/useDateFormat";
import { useMobileResponsive } from "@hooks/useDeviceType";
import type {
  ProcessedAuthorContent,
  RawImageField,
} from "@typings/Opensearch";
import { useEffect } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

export type ArticleCardElementProps = {
  hasParagraph?: boolean;
  hasDate?: boolean;
  hasDateInAgo?: boolean;
  hasQuickNews?: boolean;
  hasShowAuthor?: boolean;
};

export type ArticleCardStyleProps = {
  cardWrapper?: string;
  contentWrapper?: string;
  paragraphStyle?: string;
  titleWrapper?: string;
  titleStyle?: string;
  imageWrapperStyle?: string;
  imageStyle?: string;
};

export type ArticleCardProps = {
  linkTo: string;
  title: string;
  paragraph?: string;
  fullDate?: string;
  time?: string;
  sectionName?: string;
  displayWidth: number;
  displayHeight: number;
  imageField?: RawImageField;
  imageAlt?: string;
  urlPath?: string;
  overrideStyle?: ArticleCardStyleProps;
  elements?: ArticleCardElementProps;
  dateCreated?: string;
  dateEdited?: string;
  refMedium?: string;
  linkToQuick?: string;
  authors?: ProcessedAuthorContent[];
};

export default function ArticleCard({
  title,
  paragraph,
  fullDate,
  time,
  displayWidth,
  displayHeight,
  imageField,
  imageAlt,
  linkTo,
  overrideStyle,
  elements,
  dateEdited,
  dateCreated,
  refMedium,
  linkToQuick,
  authors,
}: ArticleCardProps) {
  const {
    cardWrapper,
    contentWrapper,
    imageWrapperStyle,
    imageStyle,
    paragraphStyle,
    titleStyle,
    titleWrapper,
  } = overrideStyle || {};
  const {
    hasParagraph = true,
    hasDate,
    hasDateInAgo = false,
    hasQuickNews = false,
    hasShowAuthor = false,
  } = elements || {};

  if (refMedium) {
    linkTo = `${linkTo}?ref=${refMedium}`;
  }
  let linkToQuickWithRef: string = linkToQuick || linkTo;
  if (refMedium) {
    linkToQuickWithRef = `${linkToQuickWithRef}?ref=${refMedium}`;
  }

  const { isMobile, mounted } = useMobileResponsive();

  useEffect(() => {
    if (hasQuickNews && linkToQuickWithRef && isMobile) {
      const quickNewsLinks = document.querySelectorAll<HTMLAnchorElement>(
        `a.quick-news-link[data-qnlink="${linkToQuickWithRef}"]`,
      );
      quickNewsLinks.forEach((quickNewsLink) => {
        quickNewsLink.setAttribute("href", linkToQuickWithRef);
      });
    }
  }, [hasQuickNews, linkToQuickWithRef, isMobile]);

  return (
    <div
      className={twMerge(
        "flex w-full gap-5 py-5 border-b border-gray-2250",
        cardWrapper,
      )}
    >
      <Link
        reloadDocument
        to={linkTo}
        data-qnlink={linkToQuickWithRef}
        className={twMerge(
          "w-full flex-shrink-0 md:flex-grow-0 sm:w-[260px]",
          imageWrapperStyle,
          hasQuickNews && "quick-news-link",
        )}
      >
        <Image
          displayWidth={displayWidth}
          srcWidth={displayWidth}
          srcHeight={displayHeight}
          src={imageField?.url ?? DefaultImage}
          alt={imageAlt ?? title}
          className={twMerge("w-full", imageStyle)}
        />
      </Link>

      <div className={twMerge("flex flex-col w-full gap-xs", contentWrapper)}>
        <div className={twMerge("flex flex-col gap-sm", titleWrapper)}>
          <Link
            reloadDocument
            to={linkTo}
            data-qnlink={linkToQuickWithRef}
            className={hasQuickNews ? "quick-news-link" : ""}
          >
            <Heading
              Element="h3"
              className={twMerge(
                "font-bold font-primary hover:underline hover:text-red-200 text-red-100",
                titleStyle,
              )}
            >
              {title}
            </Heading>
          </Link>
        </div>

        {hasParagraph && (
          <p
            className={twMerge(
              "font-regular font-primary text-base lg:text-2sm leading-300",
              paragraphStyle,
            )}
          >
            {paragraph}
          </p>
        )}

        {hasDate && (
          <Date>
            {fullDate}
            {time ? <span className="lowercase"> - {time}</span> : ""}
          </Date>
        )}
        {hasDateInAgo && dateEdited && dateCreated && mounted && (
          <Date>
            <span className="lowercase">
              {formatDateAgo(
                dateEdited > dateCreated ? dateEdited : dateCreated,
              )}
            </span>
          </Date>
        )}
        {hasShowAuthor && authors && authors.length > 0 && (
          <div className="line-clamp-1 self-stretch">
            {authors.map(
              (author, index) =>
                author.hasProfile &&
                author.urlPath && (
                  <Link
                    key={index}
                    to={author.urlPath}
                    target="_blank"
                    className="font-primary text-xs leading-[150%] text-red-100 font-medium"
                  >
                    {author.name}
                    {index < authors.length - 1 ? ", " : ""}
                  </Link>
                ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}
